import { forgotPasswordSendMail } from "../../service/authentication/AuthenticationService";

import { useState } from "react";

interface ToastState {
    visible: boolean;
    severity: string;
    message: string;
}

interface EmailResponse {
    data: {
        Error?: string;
        Result?: {
            success: boolean;
            message: string;
        };
    };
}

interface ResetPasswordData {
    Email: string;
    TenancyName: string | null;
}

export function ForgotPassword() {
    const [email, setEmail] = useState<string>("");
    const [tenancyName, setTenancyName] = useState<string | null>("");
    const [noTenancy, setNoTenancy] = useState<boolean>(false);
    const [toast, setToast] = useState<ToastState>({ visible: false, severity: "", message: "" });
    const emailValidation = /^\S+@\S+\.\S+$/;

    const handleNoTenancyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setNoTenancy(e.target.checked);
        if (e.target.checked) {
            setTenancyName(null);
        }else{
            setTenancyName("");
        }
    };

    const showToast = (severity: string, message: string): void => {
        setToast({ visible: true, severity, message });
        setTimeout(() => {
            setToast({ visible: false, severity: "", message: "" });
        }, 3000);
    };



    const sendResetPasswordMail = async (): Promise<void> => {
        // Validate email
        if (!email) {
            showToast("error", "Email is required");
            return;
        }

        if (!emailValidation.test(email)) {
            showToast("error", "Please provide a valid email address");
            return;
        }

        // Validate tenancy name
        if (!noTenancy && tenancyName === undefined) {
            showToast("error", "Please provide tenancy name or check \"No Tenancy\"");
            return;
        }

        if (tenancyName && noTenancy) {
            showToast("error", "Cannot have both tenancy name and \"No Tenancy\" checked");
            return;
        }

        try {
            // Use the mock function instead of the imported service
            const sendEmail = await forgotPasswordSendMail({
                Email: email,
                TenancyName: noTenancy ? null : tenancyName
            });

            if (sendEmail.data.Error) {
                showToast("error", sendEmail.data.Error);
                return;
            }

            if (sendEmail.data.Result?.message) {
                showToast("success", sendEmail.data.Result.message);
            } else {
                showToast("success", "Password reset instructions sent");
            }
        } catch (error) {
            showToast("error", "Failed to send reset password email");
        }
    };


    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
            {/* Toast notification */}
            {toast.visible && (
                <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${toast.severity === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
                    }`}>
                    <p>{toast.message}</p>
                </div>
            )}

            {/* Card container */}
            <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
                <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-800 text-center">Forgot Password?</h2>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Email Address*</label>
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Tenancy Name</label>
                        <input
                            type="text"
                            placeholder="Enter tenancy name"
                            value={tenancyName || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setTenancyName(e.target.value);
                                if (e.target.value) setNoTenancy(false);
                            }}
                            disabled={noTenancy}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${noTenancy ? "bg-gray-100 cursor-not-allowed" : ""
                                }`}
                        />
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="noTenancy"
                            checked={noTenancy}
                            onChange={handleNoTenancyChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="noTenancy" className="ml-2 block text-sm text-gray-700">
                            No Tenancy (clear tenancy name)
                        </label>
                    </div>

                    <button
                        onClick={sendResetPasswordMail}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
                    >
                        Reset Password
                    </button>
                </div>
            </div>
        </div>
    );
}